import { z } from 'zod';
import { AssistantError, type AssistantConfiguration } from './assistant';
import { publicAppointmentKind } from './appointment-configuration';
import { calendarAppointmentKind } from './assistant';
import { resolveResponseModel } from './models';

export const BOT_MESSAGE_VERSION = 1;
// Changes authoring/review reuse without invalidating the stored rendering contract.
export const BOT_MESSAGE_AUTHORING_REVISION = 5;
export const MESSAGE_LOCALES = ['es-PE', 'en-US'] as const;
export type MessageLocale = (typeof MESSAGE_LOCALES)[number];

interface BotMessageIntentContract {
  fields: readonly string[];
  meaning: string;
  customerPurpose: string;
}

// These are outcome contracts, not customer-facing copy. Only the writer supplies prose.
export const BOT_MESSAGE_INTENTS = {
  confirm_sales: {
    fields: ['appointment', 'when', 'name', 'phone', 'email'],
    meaning:
      'Ask one natural yes/no confirmation to pass on these contact details and arrange this appointment at the chosen time. Nothing is booked yet.',
    customerPurpose:
      'Make it easy to check the appointment and contact details in a connected sentence, then agree to sharing them and arranging it with one yes. Do not read out an unlabelled list or restart the sales pitch.',
  },
  confirm_booking: {
    fields: ['appointment', 'when', 'name', 'email'],
    meaning:
      'Ask one natural yes/no confirmation of this appointment, time and contact. Nothing is booked yet.',
    customerPurpose:
      'Help the customer recognise their chosen appointment and contact details, then confirm them with one easy answer.',
  },
  confirm_change: {
    fields: ['appointment', 'when'],
    meaning:
      'Ask one yes/no confirmation to move the existing appointment to this new time. It has not changed yet.',
    customerPurpose:
      'Make the proposed new time clear and ask whether to go ahead with that change.',
  },
  confirm_cancel: {
    fields: ['appointment', 'when'],
    meaning:
      'Ask one yes/no confirmation to cancel this existing appointment. It is not cancelled yet.',
    customerPurpose:
      'Identify the appointment to cancel and let the customer confirm without pressure to keep it.',
  },
  confirm_lead_chat: {
    fields: ['name', 'phone', 'email'],
    meaning:
      'Ask one yes/no confirmation to pass on these contact details and what was discussed to the business. Nothing has been sent yet.',
    customerPurpose:
      'Let the customer check who will receive their details and conversation, then give one simple confirmation.',
  },
  confirm_lead_email: {
    fields: ['name', 'phone', 'email'],
    meaning:
      'Ask the customer to confirm the contact details and request using the confirmation link that accompanies this message. Nothing has been sent yet.',
    customerPurpose:
      'Explain briefly what details the customer is confirming and how to use the accompanying email confirmation link.',
  },
  confirmation_link: {
    fields: ['url'],
    meaning:
      'Briefly identify this link as the place to review and confirm the proposed request.',
    customerPurpose:
      'Make the purpose of the existing confirmation link clear without adding another task.',
  },
  processing: {
    fields: [],
    meaning:
      'The customer confirmed their request and it is being processed. Briefly acknowledge and say you will report the result here. Do not claim completion or promise a deadline.',
    customerPurpose:
      'Acknowledge their yes, take ownership of coordinating what they asked for and tell them you will confirm the result here. They have nothing else to do now; do not narrate processing a request.',
  },
  failed_turn_handoff: {
    fields: [],
    meaning:
      'Automatic recovery has ended without resolving this question. An internal follow-up task contains the question and conversation. This does not prove an adviser read it, contacted the customer or booked anything. Acknowledge the wait, explain the remaining difficulty plainly, and say the conversation can continue here without repeating supplied details. Do not claim ongoing automatic work or a callback deadline.',
    customerPurpose:
      'Take ownership of the unresolved question and preserve the buyer’s effort. Never dismiss it by asking what else they want, restart qualification, claim a booked call or imply the adviser already acted.',
  },
  calendar_call_description: {
    fields: [],
    meaning:
      'Description in a confirmed call invitation: the salesperson will call the phone number the customer already provided. No questions, additional confirmation, invented numbers or extra promises.',
    customerPurpose:
      'Tell the customer what to expect for the call: the salesperson calls the number they gave.',
  },
  lead_recorded: {
    fields: [],
    meaning:
      'The business received the enquiry and contact details with conversation context. Do not claim an appointment, purchase or reservation, or promise when a person will reply.',
    customerPurpose:
      'Reassure the customer that their enquiry reached the team with the details needed to continue.',
  },
  lead_recorded_schedule: {
    fields: ['appointment'],
    meaning:
      'The business received the enquiry. Ask which day suits the customer for the configured appointment. A time still needs to be chosen and booked.',
    customerPurpose:
      'Move naturally from the received enquiry to choosing a convenient day, without asking again for contact details.',
  },
  lead_calendar_unavailable: {
    fields: [],
    meaning:
      'The business received the enquiry and contact details. The appointment time cannot yet be confirmed. No need to repeat contact details. Do not promise a booked appointment or a callback deadline.',
    customerPurpose:
      'Explain that the team has the enquiry but the time still needs confirmation, keeping the customer from repeating their details.',
  },
  request_pending: {
    fields: [],
    meaning:
      'The request was received but final confirmation is still pending. Do not claim completion or invent the cause or a deadline.',
    customerPurpose:
      'Acknowledge what the customer asked for and explain simply that you cannot confirm the result yet.',
  },
  sales_call_booked: {
    fields: ['when', 'phone'],
    meaning:
      'The adviser call is confirmed at this time and will use this phone number. Calm warm completion; no further confirmation, pitch or question.',
    customerPurpose:
      'Leave the customer clear and comfortable about when the adviser will call and which number will ring.',
  },
  appointment_booked: {
    fields: ['appointment', 'when'],
    meaning:
      'This appointment is confirmed for this time. No further confirmation, pitch or question.',
    customerPurpose:
      'Close the arrangement warmly with its confirmed appointment and time, without giving the customer more work.',
  },
  appointment_changed: {
    fields: ['appointment', 'when'],
    meaning:
      'The existing appointment was successfully moved to this time. No further confirmation or question.',
    customerPurpose:
      'Reassure the customer that the requested change is done and clearly give the new time.',
  },
  appointment_cancelled: {
    fields: ['appointment'],
    meaning:
      'The requested appointment cancellation succeeded. No additional confirmation, pressure or rebooking pitch.',
    customerPurpose:
      'Acknowledge that the appointment is cancelled and respect the customer’s decision without a celebratory or disappointed reaction.',
  },
  appointment_location: {
    fields: ['location'],
    meaning:
      'Briefly state this configured appointment location. Used both before and after confirmation: do not imply a booking or attendance has been confirmed. Do not add directions or other facts.',
    customerPurpose:
      'Make the relevant location easy to understand, without implying that the appointment has already been arranged.',
  },
  payment_reported: {
    fields: [],
    meaning:
      'The payment report was recorded for review. The payment itself is not yet confirmed. Make that distinction clear.',
    customerPurpose:
      'Reassure the customer that their payment information arrived while clearly explaining that the payment still needs checking.',
  },
  payment_proposed: {
    fields: [],
    meaning:
      'The payment proposal was recorded for review, but is not yet approved. Make that distinction clear.',
    customerPurpose:
      'Acknowledge the proposed payment arrangement and explain plainly that it still needs approval.',
  },
  dispute_recorded: {
    fields: [],
    meaning:
      'The dispute was recorded for the business to review. Do not claim it is resolved or promise a deadline.',
    customerPurpose:
      'Show that the customer’s concern has reached the team for review without minimising it or pretending it is settled.',
  },
  handoff_recorded: {
    fields: [],
    meaning:
      'The request reached the team inbox. Do not promise a human response deadline or say the question has been resolved.',
    customerPurpose:
      'Tell the customer their request reached the people who can continue helping them, without describing an inbox workflow.',
  },
  booking_waiting_lead: {
    fields: ['appointment'],
    meaning:
      'Contact details and chosen time are saved, but the business has not yet acknowledged the enquiry. The appointment is not confirmed; no need to repeat details. Say this naturally without internal system names or a promise of recovery.',
    customerPurpose:
      'Explain that you have their details and chosen time but still cannot confirm the appointment with the team; spare them another round of questions.',
  },
  booking_waiting_calendar: {
    fields: ['appointment'],
    meaning:
      'The enquiry is recorded, but the appointment time is still awaiting calendar confirmation. The appointment is not booked yet. No need to repeat details; do not invent an alternative time.',
    customerPurpose:
      'Reassure the customer that the team has their enquiry and explain that confirming the appointment time is what remains, without describing a calendar system.',
  },
  booking_waiting_record: {
    fields: ['appointment'],
    meaning:
      'The calendar reserved the time, but the booking has not yet been fully confirmed with the adviser. Clearly say final confirmation is pending. Do not imply complete success.',
    customerPurpose:
      'Explain that the time is held but you still need to confirm the appointment with the adviser, without narrating internal records.',
  },
  booking_slot_taken: {
    fields: ['appointment'],
    meaning:
      'The enquiry is recorded but the chosen time is no longer free. Ask what other day or time suits the customer. Do not request contact details again or invent alternatives.',
    customerPurpose:
      'Acknowledge that the chosen time is no longer available and make choosing another convenient time the only next step.',
  },
  preview_attention: {
    fields: [],
    meaning:
      'This proposed request needs the business to review it. Do not say it was sent or completed.',
    customerPurpose:
      'Explain simply that the proposal needs the team’s review before it can go ahead.',
  },
  status_human: {
    fields: [],
    meaning:
      'A person from the business is expected to reply next. Do not promise when they will answer or imply they are already working on it.',
    customerPurpose:
      'Address the customer directly: a person from the team is next to respond, and you are waiting for them. Do not describe the conversation as an item sitting in a queue or imply that a person is already responding.',
  },
  status_review: {
    fields: [],
    meaning:
      'A reply is waiting for the business to review it before sending. Do not claim it was sent or give a response deadline.',
    customerPurpose:
      'Explain directly that you need the team to check your answer before you can give it to the customer. Own that need in your voice instead of reporting that a reply is in a review queue.',
  },
  status_stopped: {
    fields: [],
    meaning:
      'You will not send further messages in this conversation. Do not imply someone will follow up or that a new reply is being prepared.',
    customerPurpose:
      'Respect the stop in contact: tell the customer directly that you will stop writing, without describing automation settings or implying any further reply is coming.',
  },
  status_retrying: {
    fields: [],
    meaning:
      'The answer is delayed and an actual retry is in progress. The customer does not need to resend their message. Do not claim the question is resolved or promise a deadline.',
    customerPurpose:
      'Acknowledge the delay, explain that you are trying again and keep the customer from having to send the same message twice.',
  },
  progress_reply: {
    fields: [],
    meaning:
      'Briefly acknowledge you are still preparing the answer. Do not claim any research or action completed, or promise a deadline.',
    customerPurpose:
      'Keep the customer company during the wait with a brief, personal acknowledgement that you are still on their question.',
  },
  progress_catalog: {
    fields: [],
    meaning:
      'Briefly acknowledge you are still checking the options for the customer. No claims about results or deadlines.',
    customerPurpose:
      'Let the customer know you are looking through their options in an ordinary conversational aside, without claiming a recommendation yet.',
  },
  progress_calendar: {
    fields: [],
    meaning:
      'Briefly acknowledge you are still checking available appointment times. No claims about results or deadlines.',
    customerPurpose:
      'Briefly let the customer know you are checking which times work, without presenting an unconfirmed time as available.',
  },
  progress_confirmed_request: {
    fields: [],
    meaning:
      'Briefly acknowledge you are still processing the request the customer confirmed. It is not completed yet.',
    customerPurpose:
      'Reassure the customer that you are still taking care of what they agreed to, without another question or an administrative status report.',
  },
  progress_failed: {
    fields: [],
    meaning:
      'The attempt to answer failed. Acknowledge that this question is unresolved and the conversation remains open. Do not dismiss it by asking for a different question, pretend it was answered, promise automatic recovery or human follow-up, or mention internal diagnostics.',
    customerPurpose:
      'Own the unresolved question, preserve the customer’s effort and stay available here; do not ask them to repeat supplied information or change the subject.',
  },
} as const satisfies Record<string, BotMessageIntentContract>;
export type BotMessageIntent = keyof typeof BOT_MESSAGE_INTENTS;
const intentKeys = Object.keys(BOT_MESSAGE_INTENTS) as BotMessageIntent[];
export const botMessageIntentSchema = z.enum(
  intentKeys as [BotMessageIntent, ...BotMessageIntent[]]
);
export const botMessageEntrySchema = z
  .object({
    intent: botMessageIntentSchema,
    locale: z.enum(MESSAGE_LOCALES),
    variants: z.array(z.string().trim().min(1).max(1000)).length(2),
  })
  .strict();
export const botMessageContentSchema = z
  .object({
    services: z
      .array(
        z
          .object({
            name: z.string().min(1).max(200),
            kind: z.enum(['sales_call', 'site_visit', 'appointment']),
            locale: z.enum(MESSAGE_LOCALES),
            label: z.string().trim().min(1).max(200),
          })
          .strict()
      )
      .max(40),
    version: z.literal(BOT_MESSAGE_VERSION),
    entries: z
      .array(botMessageEntrySchema)
      .length(intentKeys.length * MESSAGE_LOCALES.length),
  })
  .strict();
export type BotMessageContent = z.infer<typeof botMessageContentSchema>;

// Each required field exactly once, no other field, and no literal date, time,
// link, time zone or internal code: the engine supplies those values.
export function botMessageVariantKeepsFields(
  intent: BotMessageIntent,
  text: string
): boolean {
  const required: readonly string[] = BOT_MESSAGE_INTENTS[intent].fields;
  const fields = [...text.matchAll(/\{\{([a-z]+)\}\}/gu)].map(
    (match) => match[1]!
  );
  const prose = text.replace(/\{\{[a-z]+\}\}/gu, '');
  return !(
    fields.length !== required.length ||
    new Set(fields).size !== fields.length ||
    required.some((field) => !fields.includes(field)) ||
    fields.some((field) => !required.includes(field)) ||
    /\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/u.test(prose) ||
    /[{}<>\d]|https?:|www\.|America\/|\b(?:UTC|GMT)\b|hora (?:estándar|de Perú|de Lima)|time ?zone/iu.test(
      prose
    )
  );
}

export function validateBotMessages(
  input: unknown,
  configuration?: AssistantConfiguration
): BotMessageContent {
  const result = botMessageContentSchema.safeParse(input);
  if (!result.success) throw new AssistantError('BOT_MESSAGES_INVALID', 502);
  const seen = new Set<string>();
  for (const entry of result.data.entries) {
    const key = `${entry.locale}:${entry.intent}`;
    if (seen.has(key) || new Set(entry.variants).size !== 2)
      throw new AssistantError('BOT_MESSAGES_INVALID', 502);
    seen.add(key);
    for (const text of entry.variants)
      if (!botMessageVariantKeepsFields(entry.intent, text))
        throw new AssistantError('BOT_MESSAGES_FIELDS_INVALID', 502);
  }
  const expectedServices = configuration
    ? messageServiceNames(configuration)
    : null;
  const serviceKeys = new Set<string>();
  for (const service of result.data.services) {
    const key = JSON.stringify([service.name, service.kind, service.locale]);
    const noun =
      service.kind === 'sales_call'
        ? 'llamada'
        : service.kind === 'site_visit'
          ? 'visita'
          : 'cita';
    if (
      serviceKeys.has(key) ||
      /[{}<>]|https?:|www\./iu.test(service.label) ||
      (service.locale === 'es-PE' &&
        !service.label.toLowerCase().startsWith(noun)) ||
      (expectedServices &&
        !expectedServices.some(
          (item) => item.name === service.name && item.kind === service.kind
        ))
    )
      throw new AssistantError('BOT_MESSAGE_SERVICES_INVALID', 502);
    serviceKeys.add(key);
  }
  if (
    expectedServices &&
    serviceKeys.size !== expectedServices.length * MESSAGE_LOCALES.length
  )
    throw new AssistantError('BOT_MESSAGE_SERVICES_INVALID', 502);
  return result.data;
}

export function messageServiceNames(configuration: AssistantConfiguration) {
  const services =
    configuration.appointmentServices?.map((service) => ({
      name: service.name,
      kind: calendarAppointmentKind(service.calendar),
    })) ??
    (configuration.calendar?.serviceName
      ? [
          {
            name: configuration.calendar.serviceName,
            kind: calendarAppointmentKind(configuration.calendar),
          },
        ]
      : []);
  return services
    .filter(
      (service, index) =>
        services.findIndex(
          (item) => item.name === service.name && item.kind === service.kind
        ) === index
    )
    .sort(
      (a, b) => a.name.localeCompare(b.name) || a.kind.localeCompare(b.kind)
    );
}

// Excludes inventory, credentials and conversation data. Runtime values are never cached here.
export function botMessageSource(configuration: AssistantConfiguration) {
  const { model, reasoningEffort } = resolveResponseModel(
    configuration.responseModel
  );
  return {
    version: BOT_MESSAGE_VERSION,
    authoringRevision: BOT_MESSAGE_AUTHORING_REVISION,
    profile: configuration.profile,
    responseModel: { model, reasoningEffort },
    appointmentKind: publicAppointmentKind(configuration),
    serviceNames: messageServiceNames(configuration),
    leadDestination: configuration.leadDestination ?? 'business',
    channels: {
      chat: configuration.channels.chat.enabled,
      email: configuration.channels.email.enabled,
    },
  };
}

// Reasoning effort is part of a stored set's key, yet wording approved for a
// voice reads the same however long the model reasons in a live turn.
export function sameBotMessageWording(
  first: AssistantConfiguration,
  second: AssistantConfiguration
): boolean {
  const wording = (configuration: AssistantConfiguration) => {
    const source = botMessageSource(configuration);
    return JSON.stringify({
      ...source,
      responseModel: { model: source.responseModel.model },
    });
  };
  return wording(first) === wording(second);
}

export function renderBotMessage(
  messages: BotMessageContent,
  intent: BotMessageIntent,
  locale: MessageLocale,
  eventId: string,
  values: Readonly<Record<string, string>> = {}
): string {
  const entry = messages.entries.find(
    (entry) => entry.intent === intent && entry.locale === locale
  );
  if (!entry || !eventId) throw new AssistantError('BOT_MESSAGE_MISSING', 409);
  for (const field of BOT_MESSAGE_INTENTS[intent].fields) {
    if (!values[field]?.trim())
      throw new AssistantError('BOT_MESSAGE_VALUE_MISSING', 409);
  }
  // Event identity keeps retries stable; successive events can use another variant.
  let hash = 0;
  for (const character of `${eventId}:${intent}`)
    hash = (Math.imul(hash, 31) + character.charCodeAt(0)) >>> 0;
  return entry.variants[hash % entry.variants.length]!.replace(
    /\{\{([a-z]+)\}\}/gu,
    (_token, field: string) => values[field]!
  );
}

export function appointmentMessageLabel(
  kind: 'sales_call' | 'site_visit' | 'appointment' | null,
  locale: MessageLocale,
  messages?: BotMessageContent,
  serviceName?: string
): string {
  if (serviceName) {
    const label = messages?.services.find(
      (service) =>
        service.name === serviceName &&
        service.kind === kind &&
        service.locale === locale
    )?.label;
    if (!label) throw new AssistantError('BOT_MESSAGE_SERVICE_MISSING', 409);
    return label;
  }
  if (locale === 'es-PE')
    return kind === 'sales_call'
      ? 'llamada con el asesor'
      : kind === 'site_visit'
        ? 'visita'
        : 'cita';
  return kind === 'sales_call'
    ? 'call with the adviser'
    : kind === 'site_visit'
      ? 'visit'
      : 'appointment';
}

export function appointmentMessageTime(
  startsAt: string,
  timezone: string,
  locale: MessageLocale
): string {
  if (!Number.isFinite(Date.parse(startsAt)))
    throw new AssistantError('APPOINTMENT_TIME_INVALID', 409);
  const date = new Date(startsAt);
  const formattedDay = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)!.value;
  const time = `${part('hour')}:${part('minute')}${part('dayPeriod').toLowerCase()}`;
  const day = locale === 'es-PE' ? formattedDay.replace(',', '') : formattedDay;
  return `${day}${locale === 'es-PE' ? ' a las ' : ' at '}${time}`;
}
